import { type Entity, type Resolution } from '@api';
import {
  intradayResamples,
  ResampleIntervals,
  type TimeSeriesItem,
} from '@chart';
import { isPriceSnake, isSinglePriceEntity } from '@helpers';
import type { QueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';
import { fetchSnakeByName } from '@terminal/services/overview-widget/overview-widget-service';
import { isPriceHistoryV2Enabled } from '@terminal/services/price/price-history-flags';
import {
  getEntityPriceVenue,
  getPrices,
} from '@terminal/services/price/price-service';
import { withGcTime } from '@terminal/services/query-client/utils';
import { Tracking } from '@terminal/services/tracking';

import { createTsDataFromSnake } from '../../utils/chart-utils/chart-utils';

interface ChartTimeSeriesResult {
  data: TimeSeriesItem[] | undefined;
  fellBackToDaily: boolean;
}

interface CreateChartTimeSeriesQueryOptionsProps {
  latestAsset: Entity;
  resolution: Resolution;
  queryClient: QueryClient;
  staleTime: number;
  gcTime?: number;
}

type CreateChartPricesQueryOptionsProps = Omit<
  CreateChartTimeSeriesQueryOptionsProps,
  'queryClient'
>;

const createChartPricesQueryOptions = ({
  latestAsset,
  resolution,
  staleTime,
  gcTime,
}: CreateChartPricesQueryOptionsProps) => {
  const venue = getEntityPriceVenue(latestAsset);

  const options = queryOptions({
    queryKey: [
      'turbo-chart-prices',
      latestAsset.tag,
      latestAsset.subscribable_ticker,
      resolution,
      venue.exchange,
      venue.country,
      // The flag selects v1 vs v2 at fetch time; key on it so a mid-session
      // flag change can't reuse a response from the other API version.
      isPriceHistoryV2Enabled(),
    ],
    queryFn: () =>
      getPrices({
        ticker: latestAsset.subscribable_ticker,
        entityTag: latestAsset.tag,
        resolution,
        exchange: venue.exchange,
        country: venue.country,
      }),
    staleTime,
  });

  return withGcTime(options, gcTime);
};

export const createChartTimeSeriesQueryOptions = ({
  latestAsset,
  resolution,
  queryClient,
  staleTime,
  gcTime,
}: CreateChartTimeSeriesQueryOptionsProps) => {
  const options = queryOptions({
    queryKey: [
      'turbo-chart-ts',
      latestAsset.tag,
      latestAsset.default_snake,
      resolution,
      // Prices are fetched inside this queryFn (via createChartPricesQueryOptions),
      // so the flag must be in the parent key too — otherwise a cached series
      // from the other API version is served after a mid-session flag toggle
      // without ever re-entering the inner price fetch.
      isPriceHistoryV2Enabled(),
    ],
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Preserve the legacy chart fallback flow exactly.
    queryFn: async (): Promise<ChartTimeSeriesResult> => {
      if (isSinglePriceEntity(latestAsset)) {
        try {
          const snakeData = await fetchSnakeByName(latestAsset.default_snake);

          if (snakeData.error) {
            throw snakeData.error;
          }

          return {
            data: createTsDataFromSnake(
              snakeData.data.result.data,
              isPriceSnake(latestAsset.default_snake)
            ),
            fellBackToDaily: false,
          };
        } catch (error) {
          Tracking.captureException(error);
          throw error;
        }
      }

      try {
        const prices = await queryClient.fetchQuery(
          createChartPricesQueryOptions({
            latestAsset,
            resolution,
            staleTime,
            gcTime,
          })
        );

        if (prices.data && prices.data.length > 0) {
          return { data: prices.data, fellBackToDaily: false };
        }

        // Intraday request returned no points (e.g. asset isn't trading
        // today): retry at daily resolution before falling back to the
        // snake path. Caller is notified via `onFallbackToDaily` so it can
        // adjust horizon/resample to match the data we actually got.
        if (intradayResamples.some(item => item === resolution)) {
          const dailyPrices = await queryClient.fetchQuery(
            createChartPricesQueryOptions({
              latestAsset,
              resolution: ResampleIntervals.OneDay,
              staleTime,
              gcTime,
            })
          );

          if (dailyPrices.data && dailyPrices.data.length > 0) {
            return { data: dailyPrices.data, fellBackToDaily: true };
          }
        }
      } catch (error) {
        Tracking.captureException(error);
        return { data: undefined, fellBackToDaily: false };
      }

      try {
        const snakeData = await fetchSnakeByName(latestAsset.default_snake);
        return {
          data:
            snakeData.data &&
            createTsDataFromSnake(snakeData.data.result.data, false),
          fellBackToDaily: false,
        };
      } catch (error) {
        Tracking.captureException(error);
        throw error;
      }
    },
    staleTime,
  });

  return withGcTime(options, gcTime);
};
